import React, { useState, useEffect, useMemo, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../SEO';
import { useTranslation } from 'react-i18next';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';

const MainLayout = memo(({ isDarkMode, setIsDarkMode, articles, categories, loading }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ყველა');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleCount, setVisibleCount] = useState(6);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const filteredArticles = useMemo(() => {
    return articles.filter((art) => {
      const matchesCategory = selectedCategory === 'ყველა' || art.category === selectedCategory;
      const titleMatch = art.title ? art.title.toLowerCase().includes(searchQuery.toLowerCase()) : false;
      const summaryMatch = art.summary ? art.summary.toLowerCase().includes(searchQuery.toLowerCase()) : false;
      return matchesCategory && (titleMatch || summaryMatch);
    });
  }, [articles, selectedCategory, searchQuery]);

  const paginatedArticles = useMemo(() => {
    return filteredArticles.slice(0, visibleCount);
  }, [filteredArticles, visibleCount]);

  const sliderArticles = useMemo(() => articles.slice(0, 3), [articles]);

  useEffect(() => {
    if (sliderArticles.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderArticles.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [sliderArticles.length]);

  const loadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  const games = [
    { id: 'puzzle', icon: '🧩', name: 'პაზლი', path: '/puzzle', color: 'from-green-500 to-emerald-500' },
    { id: 'memory', icon: '🎯', name: 'მეხსიერება', path: '/memory', color: 'from-blue-500 to-cyan-500' },
    { id: 'code-quiz', icon: '💻', name: 'ქვიზი', path: '/code-quiz', color: 'from-yellow-500 to-orange-500' },
    { id: 'typing', icon: '⌨️', name: 'სწრაფი კრება', path: '/typing', color: 'from-purple-500 to-pink-500' },
    { id: 'binary', icon: '🔢', name: 'ბინარული', path: '/binary', color: 'from-red-500 to-rose-500' },
    { id: 'logic', icon: '🧠', name: 'ლოგიკა', path: '/logic', color: 'from-indigo-500 to-violet-500' },
    { id: 'dragdrop', icon: '📐', name: 'განლაგება', path: '/dragdrop', color: 'from-teal-500 to-cyan-500' },
    { id: 'bug', icon: '🐛', name: 'შეცდომები', path: '/bug', color: 'from-amber-500 to-yellow-500' },
    { id: 'keyboard', icon: '⌨️', name: 'კლავიშები', path: '/keyboard', color: 'from-slate-500 to-gray-500' },
    { id: 'code-runner', icon: '⚡', name: 'კოდი', path: '/code-runner', color: 'from-emerald-500 to-green-500' },
    { id: 'jigsaw', icon: '🧩', name: 'Jigsaw', path: '/jigsaw', color: 'from-violet-500 to-purple-500', new: true },
    { id: 'sorting', icon: '📊', name: 'სორტირება', path: '/sorting', color: 'from-blue-500 to-indigo-500', new: true },
    { id: 'math-quiz', icon: '📐', name: 'მათემატიკა', path: '/math-quiz', color: 'from-orange-500 to-red-500', new: true },
  ];

  const tools = [
    { id: 'color-picker', icon: '🎨', name: 'ფერების ამომრჩევი', path: '/color-picker' },
    { id: 'json-formatter', icon: '📋', name: 'JSON ფორმატერი', path: '/json-formatter' },
    { id: 'password-gen', icon: '🔑', name: 'პაროლების გენერატორი', path: '/password-generator' },
    { id: 'qr-code', icon: '📱', name: 'QR კოდი', path: '/qr-code' },
    { id: 'unit-converter', icon: '📏', name: 'ერთეულების გადამყვანი', path: '/unit-converter' },
    { id: 'text-diff', icon: '📝', name: 'ტექსტის შედარება', path: '/text-diff' },
    { id: 'regex-tester', icon: '🔍', name: 'RegExp ტესტერი', path: '/regex-tester' },
    { id: 'markdown', icon: '📄', name: 'Markdown Editor', path: '/markdown-editor' },
  ];

  const categoryGroups = [
    { name: '📋 მართვა', items: ['ადმინისტრატორი', 'კონფიგურატორები'] },
    { name: '🌐 ტექნიკური', items: ['ქსელო', 'SEO/ტექნიკური'] },
    { name: '🎨 დიზაინი', items: ['დიზაინი', 'UX/UI დიზაინი'] },
    { name: '🔒 უსაფრთხოება', items: ['უსაფრთხოება'] },
    { name: '📄 დოკუმენტაცია', items: ['დოკუმენტაცია'] },
    { name: '🗄️ მონაცემები', items: ['მონაცემთა ბაზები'] },
    { name: '🔌 ინტეგრაცია', items: ['API/ინტეგრაცია'] },
  ];

  const txt = {
    home: t('home') || 'მთავარი',
    all: t('common.all') || 'ყველა',
    search: t('common.search') || 'ძიება...',
    loading: t('common.loading') || 'იტვირთება...',
    noArticles: t('common.noArticles') || 'სტატიები ვერ მოიძებნა',
    readMore: t('common.readMore') || 'ვრცლად',
    loadMore: t('common.loadMore') || 'მეტი',
    justNow: t('common.justNow') || 'ახლახან',
    categories: t('common.categories') || 'კატეგორიები',
    games: 'თამაშები',
    tools: 'ინსტრუმენტები',
    scoreboard: 'რეიტინგი',
    profile: 'პროფილი',
    login: 'შესვლა',
    logout: 'გასვლა',
    dayMode: 'დღის რეჟიმი',
    nightMode: 'ღამის რეჟიმი',
    itGames: 'IT თამაშები',
    playAndTrain: 'ითამაშე და ივარჯიშე!',
    allGames: 'ყველა თამაში',
    allTools: 'ყველა ინსტრუმენტი',
    toolsTitle: '🛠️ უფასო ინსტრუმენტები',
    toolsDesc: 'სასარგებლო ინსტრუმენტები დეველოპერებისთვის',
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-955 text-gray-100' : 'bg-gray-55 text-gray-900'
    }`}>
      
      <SEO 
        title={txt.home}
        description="ტექნოლოგიური ბლოგი - სიახლეები, სტატიები, პროგრამირება და ვებ-დეველოპმენტი"
        path="/"
      />

      <Helmet>
        <title>New Innovation | {txt.home}</title>
        <meta name="description" content="სიახლეები ტექნოლოგიებში, პროგრამირება, ვებ-დეველოპმენტი და უფასო ხელსაწყოები დეველოპერებისთვის." />
      </Helmet>

      {/* HEADER */}
      <header className={`sticky top-0 z-50 transition-colors duration-300 backdrop-blur-md ${
        isDarkMode ? 'bg-gray-900/90 border-b border-gray-800' : 'bg-white/90 border-b border-gray-100'
      }`}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="text-lg font-black tracking-wider bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
            NEW INNOVATION
          </Link>
          
          <div className="flex items-center gap-2 text-xl">
            <Link to="/scoreboard" className={`px-2 py-1.5 rounded-lg hover:opacity-80 transition-all ${
              isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-yellow-600 hover:text-yellow-700'
            }`} title={txt.scoreboard}>
              🏆
            </Link>

            {user ? (
              <div className="flex items-center gap-1">
                <span className={`px-2 py-1.5 rounded-lg text-sm font-medium ${
                  isDarkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  👤 {user.displayName || user.email?.split('@')[0] || 'User'}
                </span>
                <button
                  onClick={handleLogout}
                  className={`px-2 py-1.5 rounded-lg hover:opacity-80 transition-all`}
                  title={txt.logout}
                >
                  🚪
                </button>
              </div>
            ) : (
              <Link to="/register" className={`px-2 py-1.5 rounded-lg hover:opacity-80 transition-all ${
                isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'
              }`} title={txt.login}>
                📝
              </Link>
            )}

            <Link to="/games" className={`px-2 py-1.5 rounded-lg hover:opacity-80 transition-all ${
              isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
            }`} title={txt.games}>
              🎮
            </Link>

            <Link to="/tools" className={`px-2 py-1.5 rounded-lg hover:opacity-80 transition-all ${
              isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
            }`} title={txt.tools}>
              ⚙️
            </Link>

            <button 
              onClick={() => setIsDarkMode(!isDarkMode)} 
              className="px-2 py-1.5 rounded-lg hover:opacity-70 transition-all text-sm"
              title={isDarkMode ? txt.dayMode : txt.nightMode}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>

            <div className="flex gap-1">
              <button 
                onClick={() => changeLanguage('ka')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                  i18n.language === 'ka' 
                    ? 'bg-blue-600 text-white' 
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                ქა
              </button>
              <button 
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                  i18n.language === 'en' 
                    ? 'bg-blue-600 text-white' 
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                En
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* კატეგორიები - Mobile Friendly */}
      <section className="max-w-6xl mx-auto px-4 mt-8">
        <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          📂 {txt.categories}
        </h2>
        <div className="flex flex-wrap gap-2">
          {categoryGroups.map((group, idx) => (
            <div key={idx} className="flex flex-wrap gap-2">
              {group.items.map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${cat}`}
                  className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all hover:scale-105 ${
                    isDarkMode 
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {cat}
                </Link>
              ))}
              {idx < categoryGroups.length - 1 && (
                <span className="text-gray-500 self-center text-xs">|</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* სლაიდერი */}
      {sliderArticles.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mt-5">
          <div className={`relative h-[200px] sm:h-[250px] md:h-[350px] rounded-2xl overflow-hidden shadow-2xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-200'
          }`}>
            {sliderArticles.map((slide, index) => (
              <div key={slide.id} className={`absolute inset-0 transition-opacity duration-1000 flex flex-col justify-end p-4 sm:p-6 md:p-10 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}>
                {slide.imageUrl && (
                  <>
                    <img src={slide.imageUrl} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  </>
                )}
                <div className="relative z-20 max-w-2xl text-white">
                  <span className="bg-blue-600 text-[8px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                    {slide.category}
                  </span>
                  <h2 onClick={() => navigate(`/article/${slide.id}`)} className="text-lg sm:text-xl md:text-3xl font-extrabold mt-2 mb-1.5 cursor-pointer hover:text-blue-300 transition-colors line-clamp-2">
                    {slide.title}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-300 line-clamp-2">{slide.summary}</p>
                </div>
              </div>
            ))}
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
              {sliderArticles.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all rounded-full ${
                    index === currentSlide 
                      ? 'w-6 h-1.5 bg-white' 
                      : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ✅ თამაშები - Mobile Responsive Grid */}
      <section className="max-w-6xl mx-auto px-4 mt-8">
        <div className={`rounded-2xl p-4 sm:p-5 transition-all ${
          isDarkMode ? 'bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-800/30' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                🎮 {txt.itGames}
              </h2>
              <p className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {txt.playAndTrain}
              </p>
            </div>
            <Link to="/games">
              <button className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-bold transition-all hover:scale-105 text-xs sm:text-sm ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white' 
                  : 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white'
              }`}>
                {txt.allGames} →
              </button>
            </Link>
          </div>
          
          {/* ✅ Mobile: 3 columns, Tablet: 4, Desktop: 7 */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-1.5 sm:gap-2 mt-4">
            {games.map((game) => (
              <Link
                key={game.id}
                to={game.path}
                className={`p-1.5 sm:p-2 rounded-xl text-center transition-all hover:scale-105 relative ${
                  isDarkMode ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white hover:shadow-lg'
                }`}
                title={game.name}
              >
                {game.new && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[6px] sm:text-[8px] px-1 py-0.5 rounded-full font-bold">
                    NEW
                  </span>
                )}
                <div className="text-xl sm:text-2xl">{game.icon}</div>
                <p className="text-[8px] sm:text-[10px] font-bold truncate">{game.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ინსტრუმენტები - Mobile Responsive */}
      <section className="max-w-6xl mx-auto px-4 mt-6">
        <div className={`rounded-2xl p-4 sm:p-5 transition-all ${
          isDarkMode ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border border-blue-800/30' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {txt.toolsTitle}
              </h2>
              <p className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {txt.toolsDesc}
              </p>
            </div>
            <Link to="/tools">
              <button className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-bold transition-all hover:scale-105 text-xs sm:text-sm ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white' 
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white'
              }`}>
                {txt.allTools} →
              </button>
            </Link>
          </div>
          
          {/* ✅ Mobile: 4 columns, Tablet: 6, Desktop: 8 */}
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-1.5 sm:gap-2 mt-4">
            {tools.map((tool) => (
              <Link
                key={tool.id}
                to={tool.path}
                className={`p-1.5 sm:p-2 rounded-xl text-center transition-all hover:scale-105 ${
                  isDarkMode ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white hover:shadow-lg'
                }`}
                title={tool.name}
              >
                <div className="text-xl sm:text-2xl">{tool.icon}</div>
                <p className="text-[7px] sm:text-[9px] font-bold truncate">{tool.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ძიება და ფილტრი - Mobile Friendly */}
      <section className="max-w-6xl mx-auto px-4 mt-8">
        <div className="flex flex-col md:flex-row gap-3 justify-between items-center">
          <div className="flex flex-wrap gap-1.5 w-full md:w-auto">
            <button onClick={() => setSelectedCategory('ყველა')} className={`px-2 sm:px-3 py-1 rounded-xl text-[10px] sm:text-xs font-bold transition-all ${
              selectedCategory === 'ყველა' 
                ? 'bg-blue-600 text-white shadow-lg' 
                : (isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100 border')
            }`}>
              {txt.all}
            </button>
            {categories.map((cat) => (
              <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-2 sm:px-3 py-1 rounded-xl text-[10px] sm:text-xs font-bold transition-all ${
                selectedCategory === cat 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : (isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100 border')
              }`}>
                {cat}
              </button>
            ))}
          </div>

          <div className="w-full md:w-64">
            <input 
              type="text" 
              placeholder={txt.search} 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className={`w-full px-3 py-2 rounded-xl border outline-none text-sm transition-all focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-900 border-gray-800 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              }`} 
            />
          </div>
        </div>
      </section>

      {/* სტატიები - Mobile Responsive */}
      <main className="max-w-6xl mx-auto px-4 mt-6 pb-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className={`h-40 sm:h-48 rounded-t-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                <div className={`p-4 sm:p-5 space-y-3 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-b-2xl`}>
                  <div className={`h-5 rounded w-3/4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                  <div className={`h-3.5 rounded w-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                  <div className={`h-3.5 rounded w-5/6 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                </div>
              </div>
            ))}
          </div>
        ) : paginatedArticles.length === 0 ? (
          <p className="text-center py-12 text-gray-500">{txt.noArticles}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {paginatedArticles.map((art) => (
                <article key={art.id} className={`group rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  isDarkMode ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-100'
                }`}>
                  {art.imageUrl && (
                    <div className="h-36 sm:h-44 overflow-hidden relative">
                      <img src={art.imageUrl} alt={art.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <span className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[8px] sm:text-[9px] font-bold px-2 py-0.5 rounded-md uppercase">
                        {art.category}
                      </span>
                    </div>
                  )}
                  <div className="p-3 sm:p-4">
                    <h3 onClick={() => navigate(`/article/${art.id}`)} className="text-sm sm:text-base font-bold line-clamp-2 cursor-pointer hover:text-blue-500 transition-colors mb-1.5 min-h-[40px] sm:min-h-[48px]">
                      {art.title}
                    </h3>
                    <p className={`text-xs sm:text-sm line-clamp-3 mb-3 min-h-[40px] sm:min-h-[52px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {art.summary}
                    </p>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-500/10">
                      <span className="text-[8px] sm:text-[10px] text-gray-500">
                        {art.createdAt?.seconds 
                          ? new Date(art.createdAt.seconds * 1000).toLocaleDateString('ka-GE') 
                          : txt.justNow}
                      </span>
                      <button onClick={() => navigate(`/article/${art.id}`)} className="text-[10px] sm:text-xs font-bold text-blue-500 hover:text-blue-600 transition-colors">
                        {txt.readMore} →
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {visibleCount < filteredArticles.length && (
              <div className="text-center mt-8">
                <button onClick={loadMore} className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl font-bold transition-colors text-xs sm:text-sm">
                  {txt.loadMore} ↓
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
});

export default MainLayout;